import React, { useState, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../../shared/components/ui/Button';
import Multiselect from 'multiselect-react-dropdown';
import toast from 'react-hot-toast';
import { getAllWorkTypes } from '../../services/api/implementation/impl';
import { useQuery } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { capitalizeWords } from '../../utils/stringUtils';

const BulkCreateIndependentTaskTemplate = ({
  onClose,
  onSubmit,
  isSubmitting,
  projectId,
  templateName,
  data, // New prop for prefilling data
  mode = 'create' // New prop: 'create' or 'edit'
}) => {
  const userId = Cookies.get('userId');

  // Initialize rows based on mode and available data
  const getInitialRows = () => {
    if (mode === 'edit' && data && data.length > 0) {
      // Edit mode: Pre-fill with existing data
      return data.map((task, index) => ({
        id: task.id || index + 1, // Use existing ID or generate one
        workType_id: task.workType_id || '', // Include the workType_id for existing tasks
        task_name: task.name || '',
        duration: task.duration ? task.duration.toString() : '',
        task_ids:
          task.dependencies?.map((dep) => ({
            id: dep.dependency?.id,
            name: dep.dependency?.name
          })) || [],
        idleTime: task.delay ? task.delay.toString() : '',
        floorwise: task.floorwise ? 'Yes' : 'No',
        working_order: task.working_order ? task.working_order.toString() : '',
        drawingRequired: task.drawing_required ? 'Yes' : 'No',
        subActivities: task.subActivity || []
      }));
    } else {
      // Create mode: Default dummy rows
      return [
        {
          id: 1,
          workType_id: '',
          task_name: '',
          duration: '',
          task_ids: [],
          idleTime: '',
          floorwise: 'No',
          working_order: '',
          drawingRequired: 'No',
          subActivities: []
        },
        {
          id: 2,
          workType_id: '',
          task_name: '',
          duration: '',
          task_ids: [],
          idleTime: '',
          floorwise: 'No',
          working_order: '',
          drawingRequired: 'No',
          subActivities: []
        },
        {
          id: 3,
          workType_id: '',
          task_name: '',
          duration: '',
          task_ids: [],
          idleTime: '',
          floorwise: 'No',
          working_order: '',
          drawingRequired: 'No',
          subActivities: []
        },
        {
          id: 4,
          workType_id: '',
          task_name: '',
          duration: '',
          task_ids: [],
          idleTime: '',
          floorwise: 'No',
          working_order: '',
          drawingRequired: 'No',
          subActivities: []
        },
        {
          id: 5,
          workType_id: '',
          task_name: '',
          duration: '',
          task_ids: [],
          idleTime: '',
          floorwise: 'No',
          working_order: '',
          drawingRequired: 'No',
          subActivities: []
        }
      ];
    }
  };

  const [nextId, setNextId] = useState(
    mode === 'edit' && data && data.length > 0 ? data.length + 1 : 6
  );
  const [rows, setRows] = useState(getInitialRows());

  const { data: AllWorkTypes } = useQuery({
    queryKey: ['AllWorkTypes'],
    queryFn: getAllWorkTypes
  });

  const workTypes =
    AllWorkTypes?.filter(
      (item) => item.type === 'Accommodation' || item.type === 'Accomodation'
    )?.map((item) => ({
      label: item.name || '',
      id: item.id,
      value: item.id
    })) || [];

  // Track page view
  useEffect(() => {
    mixpanel.track('Bulk Create Independent Task Template Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  // Create list of tasks from current form only
  const filteredTasks = useMemo(() => {
    return rows
      .filter((row) => row.task_name.trim())
      .map((row) => ({
        name: row.task_name.trim(),
        id: `new_${row.id}`,
        isNew: true,
        rowId: row.id
      }));
  }, [rows]);

  // Sync task_ids with current task names when they change
  React.useEffect(() => {
    rows.forEach((row) => {
      if (row.task_ids && row.task_ids.length > 0) {
        const hasUpdatedTask = row.task_ids.some((selectedTask) => {
          // Check for new tasks (with isNew flag and rowId)
          if (selectedTask.isNew && selectedTask.rowId) {
            const sourceTask = rows.find((r) => r.id === selectedTask.rowId);
            return sourceTask && selectedTask.name !== sourceTask.task_name.trim();
          }
          // Check for prefilled/existing tasks (numeric ID matching row.id)
          if (selectedTask.id && typeof selectedTask.id === 'number') {
            const sourceTask = rows.find((r) => r.id === selectedTask.id);
            return sourceTask && selectedTask.name !== sourceTask.task_name.trim();
          }
          return false;
        });

        if (hasUpdatedTask) {
          setRows((prevRows) => {
            return prevRows.map((r) => {
              if (r.id === row.id && r.task_ids && r.task_ids.length > 0) {
                const updatedTaskIds = r.task_ids.map((selectedTask) => {
                  // Update new tasks (with isNew flag and rowId)
                  if (selectedTask.isNew && selectedTask.rowId) {
                    const sourceTask = prevRows.find((row) => row.id === selectedTask.rowId);
                    if (sourceTask) {
                      return {
                        ...selectedTask,
                        name: sourceTask.task_name.trim()
                      };
                    }
                  }
                  // Update prefilled/existing tasks (numeric ID matching row.id)
                  if (selectedTask.id && typeof selectedTask.id === 'number') {
                    const sourceTask = prevRows.find((row) => row.id === selectedTask.id);
                    if (sourceTask) {
                      return {
                        ...selectedTask,
                        name: sourceTask.task_name.trim()
                      };
                    }
                  }
                  return selectedTask;
                });
                return { ...r, task_ids: updatedTaskIds };
              }
              return r;
            });
          });
        }
      }
    });
  }, [rows.map((r) => r.task_name).join('|')]);

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        id: mode === 'edit' ? `new_${nextId}` : nextId, // Use string ID for new tasks in edit mode
        workType_id: '',
        task_name: '',
        duration: '',
        task_ids: [],
        idleTime: '',
        floorwise: 'No',
        working_order: '',
        drawingRequired: 'No',
        subActivities: []
      }
    ]);
    setNextId(nextId + 1);
  };

  const handleRemoveRow = (id) => {
    if (rows.length > 1) {
      // Remove the row and also remove it from all dependencies
      setRows((prevRows) => {
        const filteredRows = prevRows.filter((row) => row.id !== id);

        // Remove the deleted task from all other tasks' dependencies
        return filteredRows.map((row) => {
          if (row.task_ids && row.task_ids.length > 0) {
            const updatedTaskIds = row.task_ids.filter((selectedTask) => {
              // Remove if it matches the deleted row by rowId (new tasks)
              if (selectedTask.isNew && selectedTask.rowId === id) {
                return false;
              }
              // Remove if it matches the deleted row by id (existing/prefilled tasks)
              if (selectedTask.id && selectedTask.id === id) {
                return false;
              }
              // Remove if it's a prefilled task that matches the deleted row's id
              if (
                !selectedTask.isNew &&
                selectedTask.id &&
                prevRows.find((r) => r.id === id && r.id === selectedTask.id)
              ) {
                return false;
              }
              return true;
            });
            return { ...row, task_ids: updatedTaskIds };
          }
          return row;
        });
      });
    } else {
      toast.error('At least one row is required');
    }
  };

  const handleFieldChange = (id, field, value) => {
    setRows(
      rows.map((row) => {
        if (row.id === id) {
          // Reset working_order and task_ids when floorwise changes
          if (field === 'floorwise') {
            return {
              ...row,
              [field]: value,
              working_order: '',
              task_ids: []
            };
          }
          return { ...row, [field]: value };
        }
        return row;
      })
    );
  };

  const handleAddSubActivity = (rowId, subActivity) => {
    if (subActivity.trim()) {
      setRows(
        rows.map((row) => {
          if (row.id === rowId) {
            return {
              ...row,
              subActivities: [...row.subActivities, capitalizeWords(subActivity.trim())]
            };
          }
          return row;
        })
      );
      return true;
    }
    return false;
  };

  const handleRemoveSubActivity = (rowId, index) => {
    setRows(
      rows.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            subActivities: row.subActivities.filter((_, i) => i !== index)
          };
        }
        return row;
      })
    );
  };

  const validateRows = () => {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (!row.workType_id) {
        toast.error(`Row ${i + 1}: WorkType is required`);
        return false;
      }
      if (!row.task_name.trim()) {
        toast.error(`Row ${i + 1}: Task name is required`);
        return false;
      }
      if (!row.duration) {
        toast.error(`Row ${i + 1}: Duration is required`);
        return false;
      }
      if (!row.idleTime) {
        toast.error(`Row ${i + 1}: Idle time is required`);
        return false;
      }
      if (row.floorwise === 'Yes' && !row.working_order) {
        toast.error(`Row ${i + 1}: Working order is required when floorwise is Yes`);
        return false;
      }
      if (row.floorwise === 'Yes' && row.task_ids && row.task_ids.length > 0) {
        const hasFloorwiseTask = row.task_ids.some((selectedTask) => {
          // Check if it's a new task being created in current table
          if (selectedTask.isNew) {
            const newTask = rows.find((r) => r.id === selectedTask.rowId);
            return newTask && newTask.floorwise === 'Yes';
          }
          // Check if it's an existing task from the database
          if (selectedTask.id) {
            // First check if it's in the current rows being edited
            const existingTask = rows.find((r) => r.id === selectedTask.id);
            if (existingTask) {
              return existingTask.floorwise === 'Yes';
            }
            // If not in current rows, check original data (in edit mode)
            if (mode === 'edit' && data) {
              const originalTask = data.find((t) => t.id === selectedTask.id);
              return originalTask && originalTask.floorwise === true;
            }
          }
          return false;
        });

        if (!hasFloorwiseTask) {
          toast.error(
            `Row ${i + 1}: When Floorwise is Yes, at least one dependency task must be floorwise`
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmitForm = () => {
    if (!validateRows()) {
      return;
    }

    const payloads = rows.map((row) => {
      const basePayload = {
        name: capitalizeWords(row.task_name),
        delay: Number(row.idleTime),
        duration: Number(row.duration),
        dependency: row.task_ids?.map((item) => capitalizeWords(item.name)) || [],
        drawing_required: row.drawingRequired === 'Yes',
        subActivity: row.subActivities.map((activity) => capitalizeWords(activity)),
        accomodationType: 'Independent',
        floorwise: row.floorwise === 'Yes',
        working_order: row.working_order || null,
        expandable: row.floorwise === 'Yes',
        project_id: projectId
      };

      // If row has an ID from existing data, include it for updates
      if (
        row.id &&
        typeof row.id === 'number' &&
        mode === 'edit' &&
        !row.id.toString().startsWith('new_')
      ) {
        return {
          ...basePayload,
          id: row.id,
          workType_id: row.workType_id // Include the original workType_id for existing tasks
        };
      } else {
        // For new tasks, include workType_id and accomodationType, but NO id
        return {
          ...basePayload,
          workType_id: row.workType_id,
          accomodationType: 'Independent'
        };
      }
    });

    onSubmit(payloads);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Table */}
      <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[50px]">
                #
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[150px]">
                WorkType *
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[150px]">
                Task Name *
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[100px]">
                Duration (days) *
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[200px]">
                Dependency Task
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[100px]">
                Idle Time (days) *
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[100px]">
                Floorwise *
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[150px]">
                Working Order
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[120px]">
                Drawing Required *
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[200px]">
                Subactivity
              </th>
              <th className="border border-gray-300 px-2 py-2 text-sm font-semibold min-w-[80px]">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <TableRow
                key={row.id}
                row={row}
                rowNumber={index + 1}
                workTypes={workTypes}
                filteredTasks={filteredTasks.filter((task) => {
                  // Allow self-dependency only if the task is floorwise
                  if (task.isNew && task.rowId === row.id) {
                    return row.floorwise === 'Yes';
                  }
                  return true;
                })} // Exclude current row from its own dependencies unless floorwise
                onFieldChange={handleFieldChange}
                onRemove={handleRemoveRow}
                onAddSubActivity={handleAddSubActivity}
                onRemoveSubActivity={handleRemoveSubActivity}
                canDelete={rows.length > 1}
                mode={mode}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Buttons */}
      <div className="flex justify-between items-center">
        <Button type="button" onClick={handleAddRow} customStyle={{ padding: '0.5rem 1rem' }}>
          Add More Row
        </Button>
        <div className="flex gap-4">
          <Button type="button" onClick={onClose} displayType="secondary">
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmitForm} disabled={isSubmitting}>
            Submit All
          </Button>
        </div>
      </div>
    </div>
  );
};

// Separate Row Component
const TableRow = ({
  row,
  rowNumber,
  workTypes,
  filteredTasks,
  onFieldChange,
  onRemove,
  onAddSubActivity,
  onRemoveSubActivity,
  canDelete,
  mode = 'create'
}) => {
  const [subActivityInput, setSubActivityInput] = useState('');

  const handleAddSub = () => {
    if (onAddSubActivity(row.id, subActivityInput)) {
      setSubActivityInput('');
    }
  };

  // Determine if this is an existing task (has numeric ID and is in edit mode)
  const isExistingTask =
    mode === 'edit' &&
    row.id &&
    typeof row.id === 'number' &&
    !row.id.toString().startsWith('new_');

  return (
    <tr
      className={
        isExistingTask ? 'bg-blue-50' : mode === 'edit' ? 'bg-gray-50' : 'hover:bg-gray-50'
      }>
      {/* Row Number */}
      <td className="border border-gray-300 px-2 py-2 text-center font-medium">{rowNumber}</td>
      {/* WorkType */}
      <td className="border border-gray-300 px-2 py-2">
        <select
          value={row.workType_id}
          onChange={(e) => onFieldChange(row.id, 'workType_id', e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
          <option value="">Select WorkType</option>
          {workTypes.map((wt) => (
            <option key={wt.id} value={wt.id}>
              {wt.label}
            </option>
          ))}
        </select>
      </td>

      {/* Task Name */}
      <td className="border border-gray-300 px-2 py-2">
        <input
          type="text"
          value={row.task_name}
          onChange={(e) => onFieldChange(row.id, 'task_name', e.target.value)}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          placeholder="Enter task name"
        />
      </td>

      {/* Duration */}
      <td className="border border-gray-300 px-2 py-2">
        <input
          type="text"
          value={row.duration}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/[^0-9]/g, '');
            onFieldChange(row.id, 'duration', cleaned);
          }}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          placeholder="Days"
        />
      </td>

      {/* Dependency Task */}
      <td className="border border-gray-300 px-2 py-2">
        <Multiselect
          options={filteredTasks || []}
          selectedValues={row.task_ids}
          onSelect={(selectedList) => onFieldChange(row.id, 'task_ids', selectedList)}
          onRemove={(selectedList) => onFieldChange(row.id, 'task_ids', selectedList)}
          displayValue="name"
          placeholder="Select tasks"
          showCheckbox
          style={{
            multiselectContainer: { width: '100%' },
            searchBox: { minHeight: '32px', fontSize: '0.875rem' },
            chips: { fontSize: '0.75rem', padding: '0.25rem 0.5rem' }
          }}
        />
      </td>

      {/* Idle Time */}
      <td className="border border-gray-300 px-2 py-2">
        <input
          type="text"
          value={row.idleTime}
          onChange={(e) => {
            const cleaned = e.target.value.replace(/[^0-9]/g, '');
            onFieldChange(row.id, 'idleTime', cleaned);
          }}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
          placeholder="Days"
        />
      </td>

      {/* Floorwise */}
      <td className="border border-gray-300 px-2 py-2">
        <div className="flex items-center gap-2">
          <label className="flex items-center space-x-1 cursor-pointer">
            <input
              type="radio"
              name={`floorwise_${row.id}`}
              value="Yes"
              checked={row.floorwise === 'Yes'}
              onChange={(e) => onFieldChange(row.id, 'floorwise', e.target.value)}
              className="cursor-pointer"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center space-x-1 cursor-pointer">
            <input
              type="radio"
              name={`floorwise_${row.id}`}
              value="No"
              checked={row.floorwise === 'No'}
              onChange={(e) => onFieldChange(row.id, 'floorwise', e.target.value)}
              className="cursor-pointer"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
      </td>

      {/* Working Order */}
      <td className="border border-gray-300 px-2 py-2">
        <select
          value={row.working_order}
          onChange={(e) => onFieldChange(row.id, 'working_order', e.target.value)}
          disabled={row.floorwise !== 'Yes'}
          className={`w-full border border-gray-300 rounded px-2 py-1 text-sm ${
            row.floorwise !== 'Yes' ? 'bg-gray-100 cursor-not-allowed' : ''
          }`}>
          <option value="">Select</option>
          <option value="Top Down">Top Down</option>
          <option value="Bottom Up">Bottom Up</option>
        </select>
      </td>

      {/* Drawing Required */}
      <td className="border border-gray-300 px-2 py-2">
        <div className="flex items-center gap-2">
          <label className="flex items-center space-x-1 cursor-pointer">
            <input
              type="radio"
              name={`drawingRequired_${row.id}`}
              value="Yes"
              checked={row.drawingRequired === 'Yes'}
              onChange={(e) => onFieldChange(row.id, 'drawingRequired', e.target.value)}
              className="cursor-pointer"
            />
            <span className="text-sm">Yes</span>
          </label>
          <label className="flex items-center space-x-1 cursor-pointer">
            <input
              type="radio"
              name={`drawingRequired_${row.id}`}
              value="No"
              checked={row.drawingRequired === 'No'}
              onChange={(e) => onFieldChange(row.id, 'drawingRequired', e.target.value)}
              className="cursor-pointer"
            />
            <span className="text-sm">No</span>
          </label>
        </div>
      </td>

      {/* Subactivity */}
      <td className="border border-gray-300 px-2 py-2">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={subActivityInput}
              onChange={(e) => setSubActivityInput(e.target.value)}
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
              placeholder="Add subactivity"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddSub();
                }
              }}
            />
            <button
              type="button"
              onClick={handleAddSub}
              className="bg-[#2A565E] text-white px-2 py-1 rounded text-sm hover:bg-[#1f4147]">
              +
            </button>
          </div>
          {row.subActivities.length > 0 && (
            <div className="flex flex-wrap gap-1 max-h-[80px] overflow-y-auto">
              {row.subActivities.map((sub, idx) => (
                <div
                  key={idx}
                  className="flex items-center bg-gray-200 rounded px-2 py-0.5 text-xs">
                  <span>{sub}</span>
                  <button
                    type="button"
                    onClick={() => onRemoveSubActivity(row.id, idx)}
                    className="ml-1 text-red-500 font-bold">
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </td>

      {/* Action */}
      <td className="border border-gray-300 px-2 py-2 text-center">
        <button
          type="button"
          onClick={() => onRemove(row.id)}
          disabled={!canDelete}
          className={`text-red-500 hover:text-red-700 ${
            !canDelete ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          title="Delete row">
          <Trash2 size={16} />
        </button>
      </td>
    </tr>
  );
};

export default BulkCreateIndependentTaskTemplate;
